<script>
/**
 * Who changed what, and what it said before.
 *
 * ── Why the "before" is the point ─────────────────────────────────────────
 *
 * `kb_documents.updated_by` already answered "who touched this last". What it
 * could never answer is "what did it say on Tuesday, when the customer was
 * told the wrong figure" — the next save overwrote both the text and the name.
 * So the old value is the column this screen exists for, and it is shown
 * beside the new one rather than behind a click.
 *
 * ── Not the FAQ audit ─────────────────────────────────────────────────────
 *
 * The knowledge base has its own "audit" which grades the FAQ's quality. This
 * is the change log. Same English word, unrelated questions — hence the
 * separate screen, the separate path (`/changes`), and the subtitle saying so
 * in plain words.
 *
 * ── Scoped to one brand ───────────────────────────────────────────────────
 *
 * Changes belong to a brand's FAQ, so "all brands" has nothing to show. Same
 * as the knowledge base editor: ask for a brand rather than render an empty
 * table that looks like "nobody has ever changed anything".
 */
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import i18n from '@/i18n'
import { sharedHelper, tenantHelper, filtersHelper } from '@/utils'
import { auditLogService } from '@/helpers/services/auditLog.service'

const PAGE_SIZE = 50

export default {
  page: { title: i18n.t('module.auditLog') },
  components: { Layout, PageHeader, MultiSelect },
  data() {
    return {
      tenantId: null,
      rows: [],
      total: 0,
      offset: 0,
      limit: PAGE_SIZE,
      loading: true,
      actorList: [],
      // null, not '': MultiSelect clears a single-select back to null, and
      // the service drops null and '' alike.
      filters: { actor: null, action: null, entity_id: '', since: '', until: '' },
      // The row opened for its full before/after, and its fetched detail.
      openId: null,
      detail: null,
      detailLoading: false,
    }
  },
  computed: {
    breadcrumbItems() {
      return [{ text: i18n.t('module.admin') }, { text: i18n.t('module.auditLog'), active: true }]
    },
    /** 1-based, for "showing 1–50 of 214". */
    from() {
      return this.total === 0 ? 0 : this.offset + 1
    },
    to() {
      return Math.min(this.offset + this.limit, this.total)
    },
    hasPrev() {
      return this.offset > 0
    },
    hasNext() {
      return this.offset + this.limit < this.total
    },
    actorDdl() {
      return this.actorList.map((a) => ({ text: a, value: a }))
    },
    actionDdl() {
      return ['create', 'update', 'delete', 'publish'].map((a) => ({
        text: this.actionLabel(a),
        value: a,
      }))
    },
    anyFilter() {
      return Object.values(this.filters).some((v) => v !== '' && v !== null)
    },
  },
  mounted() {
    this.tenantId = tenantHelper.current()
    if (this.tenantId === null) {
      this.loading = false
      return
    }
    this.load()
    this.loadActors()
  },
  methods: {
    async load() {
      if (this.tenantId === null) return
      this.loading = true
      // A filter change while a row is open would leave the detail of a row
      // that is no longer in the list.
      this.openId = null
      this.detail = null
      try {
        const res = await auditLogService.list(this.tenantId, {
          ...this.filters,
          limit: this.limit,
          offset: this.offset,
        })
        const d = (res && res.data) || {}
        this.rows = d.entries || []
        this.total = Number(d.total || 0)
      } catch (e) {
        this.rows = []
        this.total = 0
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.loading = false
      }
    },

    async loadActors() {
      try {
        const res = await auditLogService.actors(this.tenantId)
        this.actorList = (res && res.data) || []
      } catch (e) {
        // The dropdown is a convenience; losing it must not lose the page.
        this.actorList = []
      }
    },

    applyFilters() {
      this.offset = 0
      this.load()
    },

    clearFilters() {
      this.filters = { actor: null, action: null, entity_id: '', since: '', until: '' }
      this.offset = 0
      this.load()
    },

    prev() {
      this.offset = Math.max(0, this.offset - this.limit)
      this.load()
    },

    next() {
      this.offset = this.offset + this.limit
      this.load()
    },

    /**
     * Open a row for its full before/after.
     *
     * The list carries only the first 200 characters of each value — fifty
     * full FAQ bodies in one response is a page nobody waits for — so the
     * whole text is fetched for the single row somebody actually asked about.
     */
    async toggle(row) {
      if (this.openId === row.id) {
        this.openId = null
        this.detail = null
        return
      }
      this.openId = row.id
      this.detail = null
      this.detailLoading = true
      try {
        const res = await auditLogService.get(this.tenantId, row.id)
        this.detail = (res && res.data) || null
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
        this.openId = null
      } finally {
        this.detailLoading = false
      }
    },

    readError(e) {
      const d = e && e.response && e.response.data
      return (d && (d.error || d.message)) || i18n.t('message.error')
    },

    when(iso) {
      if (!iso) return '—'
      return filtersHelper.getDateTimeFormat(iso)
    },

    /** An action ai_bot adds later renders as itself, not as a missing key. */
    actionLabel(a) {
      const key = 'auditLog.action_' + a
      return this.$te(key) ? this.$t(key) : a
    },

    entityLabel(e) {
      const key = 'auditLog.entity_' + e
      return this.$te(key) ? this.$t(key) : e
    },

    fieldLabel(f) {
      if (!f) return '—'
      const key = 'auditLog.field_' + f
      return this.$te(key) ? this.$t(key) : f
    },

    actionClass(a) {
      if (a === 'delete') return 'badge-soft-danger'
      if (a === 'create') return 'badge-soft-success'
      if (a === 'publish') return 'badge-soft-info'
      return 'badge-soft-warning'
    },

    /** Empty is a real state here: "was nothing" and "became nothing" both
     *  mean something, and rendering them blank hides it. */
    shown(v) {
      if (v === null || v === undefined) return i18n.t('auditLog.nothing')
      if (v === '') return i18n.t('auditLog.empty')
      return v
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="$t('module.auditLog')" :items="breadcrumbItems" />

    <div class="card">
      <div class="card-body">
        <p class="small text-muted mb-3">{{ $t('auditLog.hint') }}</p>

        <div v-if="tenantId === null" class="small text-muted">
          {{ $t('auditLog.pickBrand') }}
        </div>

        <template v-else>
          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-3 col-12 mb-2">
              <label class="small text-muted mb-1 d-block">{{ $t('auditLog.who') }}</label>
              <MultiSelect
                v-model="filters.actor"
                :options="actorDdl"
                :multiple="false"
                :placeholder="$t('auditLog.anyone')"
                @change="applyFilters"
              />
            </div>
            <div class="col-md-2 col-12 mb-2">
              <label class="small text-muted mb-1 d-block">{{ $t('auditLog.what') }}</label>
              <MultiSelect
                v-model="filters.action"
                :options="actionDdl"
                :multiple="false"
                :placeholder="$t('auditLog.anyAction')"
                @change="applyFilters"
              />
            </div>
            <div class="col-md-3 col-12 mb-2">
              <label class="small text-muted mb-1 d-block">{{ $t('auditLog.which') }}</label>
              <input
                v-model.trim="filters.entity_id"
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('auditLog.whichPlaceholder')"
                @change="applyFilters"
              />
            </div>
            <div class="col-md-2 col-6 mb-2">
              <label class="small text-muted mb-1 d-block">{{ $t('auditLog.from') }}</label>
              <input v-model="filters.since" type="date" class="form-control form-control-sm" @change="applyFilters" />
            </div>
            <div class="col-md-2 col-6 mb-2">
              <label class="small text-muted mb-1 d-block">{{ $t('auditLog.to') }}</label>
              <input v-model="filters.until" type="date" class="form-control form-control-sm" @change="applyFilters" />
            </div>
          </div>

          <div v-if="anyFilter" class="mb-3">
            <button class="btn btn-sm btn-outline-secondary" @click="clearFilters">
              {{ $t('label.clearFilters') }}
            </button>
          </div>

          <div v-if="loading" class="small text-muted py-3">{{ $t('label.loading') }}</div>
          <div v-else-if="!rows.length" class="small text-muted py-3">
            {{ anyFilter ? $t('auditLog.emptyFiltered') : $t('auditLog.emptyAll') }}
          </div>

          <div v-else class="table-responsive">
            <table class="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style="width: 160px">{{ $t('auditLog.when') }}</th>
                  <th style="width: 190px">{{ $t('auditLog.who') }}</th>
                  <th style="width: 90px">{{ $t('auditLog.what') }}</th>
                  <th>{{ $t('auditLog.which') }}</th>
                  <th style="width: 120px">{{ $t('auditLog.field') }}</th>
                  <th style="width: 90px"></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="r in rows">
                  <tr :key="r.id" class="log-row" @click="toggle(r)">
                    <td class="small text-muted">{{ when(r.createdAt) }}</td>
                    <td class="small">{{ r.actor }}</td>
                    <td>
                      <span class="badge" :class="actionClass(r.action)">{{ actionLabel(r.action) }}</span>
                    </td>
                    <td class="small">
                      <code>{{ r.entityId }}</code>
                      <span class="text-muted ml-1">{{ entityLabel(r.entity) }}</span>
                      <div v-if="r.summary" class="text-muted">{{ r.summary }}</div>
                    </td>
                    <td class="small text-muted">{{ fieldLabel(r.field) }}</td>
                    <td class="small text-right">
                      <span class="text-info">
                        {{ openId === r.id ? $t('auditLog.hide') : $t('auditLog.compare') }}
                      </span>
                    </td>
                  </tr>

                  <tr v-if="openId === r.id" :key="r.id + '-detail'">
                    <td colspan="6" class="diff-cell">
                      <div v-if="detailLoading" class="small text-muted">{{ $t('label.loading') }}</div>
                      <div v-else-if="detail" class="row">
                        <div class="col-md-6 col-12 mb-2">
                          <div class="small text-muted mb-1">{{ $t('auditLog.before') }}</div>
                          <pre class="diff diff-old">{{ shown(detail.oldValue) }}</pre>
                        </div>
                        <div class="col-md-6 col-12 mb-2">
                          <div class="small text-muted mb-1">{{ $t('auditLog.after') }}</div>
                          <pre class="diff diff-new">{{ shown(detail.newValue) }}</pre>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-if="!loading && total > 0" class="d-flex align-items-center justify-content-between mt-3">
            <span class="small text-muted">{{ $t('auditLog.showing', [from, to, total]) }}</span>
            <span>
              <button class="btn btn-sm btn-outline-secondary mr-1" :disabled="!hasPrev" @click="prev">
                {{ $t('auditLog.newer') }}
              </button>
              <button class="btn btn-sm btn-outline-secondary" :disabled="!hasNext" @click="next">
                {{ $t('auditLog.older') }}
              </button>
            </span>
          </div>
        </template>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.log-row {
  cursor: pointer;
}
.log-row:hover {
  background: #f6f7fb;
}
.diff-cell {
  background: #fafbfe;
}
.diff {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 320px;
  overflow: auto;
  font-size: 12px;
  padding: 8px 10px;
  border-radius: 4px;
  margin: 0;
}
/* The two sides are colour-coded, but each is also labelled above it —
   colour alone would not say which is which to anyone who cannot see it. */
.diff-old {
  background: #fdf1f0;
  border: 1px solid #f3d6d3;
}
.diff-new {
  background: #eef8f2;
  border: 1px solid #cfe9db;
}
</style>
